import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from 'next-themes';
import { Eye, EyeOff, Sun, Moon, Globe, LogIn, Loader } from 'lucide-react';
import { ValorizaLogo } from '../components/ValorizaLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { msalInstance, loginRequest } from '../msalConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/c0f625ea-80c2-4aec-95b7-d3faaf8e69df/images/9f55cda8f08f1d366464524133ffbdbc72224e57ecf9e42382fa896deb676d02.png';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();

  const { login, loginWithToken } = useAuth();
  const { lang, toggleLang } = useLanguage();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  // Handle MSAL redirect callback on page load
  useEffect(() => {
    msalInstance.initialize().then(() => {
      msalInstance.handleRedirectPromise().then(async (response) => {
        if (response && response.accessToken) {
          setSsoLoading(true);
          try {
            const res = await fetch(`${API_URL}/api/auth/sso/msal-token`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ access_token: response.accessToken }),
            });
            const data = await res.json();
            if (data.access_token) {
              const userData = await loginWithToken(data.access_token);
              // Explicit navigation based on role (more reliable than relying on re-render)
              if (userData?.role === 'admin') navigate('/admin', { replace: true });
              else if (userData?.role === 'manager') navigate('/manager', { replace: true });
              else navigate('/portal', { replace: true });
            } else {
              setError(data.detail || 'Error en la autenticación SSO');
            }
          } catch (e) {
            setError('Error al procesar la autenticación Microsoft');
          } finally {
            setSsoLoading(false);
          }
        }
      }).catch((e) => {
        console.error('MSAL redirect error:', e);
      });
    });

    const ssoError = searchParams.get('sso_error');
    if (ssoError) setError(decodeURIComponent(ssoError));
  }, [searchParams, navigate, loginWithToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'manager') navigate('/manager');
      else navigate('/portal');
    } catch (err) {
      setError(err.response?.data?.detail || (lang === 'es' ? 'Email o contraseña incorrectos' : 'Incorrect email or password'));
    } finally {
      setLoading(false);
    }
  };

  const handleSSO = async () => {
    setSsoLoading(true);
    setError('');
    try {
      // Clear stale MSAL state from previous attempts to avoid code reuse errors
      Object.keys(sessionStorage)
        .filter(k => k.startsWith('msal.'))
        .forEach(k => sessionStorage.removeItem(k));

      await msalInstance.initialize();
      // Use the default redirectUri from msalConfig (/login) — already registered in Azure.
      // loginRedirect keeps sessionStorage in the same window, so PKCE works correctly.
      await msalInstance.loginRedirect({
        ...loginRequest,
        redirectUri: window.location.origin + '/login',
      });
      // Browser navigates away — no code executes after this
    } catch (e) {
      setSsoLoading(false);
      if (e?.errorCode !== 'user_cancelled') {
        setError(lang === 'es' ? 'Error al conectar con Microsoft. Inténtalo de nuevo.' : 'Error connecting to Microsoft. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: Form */}
      <div className="w-full md:w-[45%] flex flex-col justify-center items-center px-8 py-12 bg-background relative">
        {/* Controls */}
        <div className="absolute top-4 right-4 flex items-center gap-1.5">
          <Button variant="ghost" size="sm" onClick={toggleLang} data-testid="lang-toggle-login" className="gap-1 text-xs h-8 px-2">
            <Globe className="h-3.5 w-3.5" />{lang.toUpperCase()}
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="h-8 w-8" data-testid="theme-toggle-login">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </div>

        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="mb-10 flex flex-col items-start">
            <ValorizaLogo height="h-10" className="mb-6" />
            <h1 className="text-3xl font-bold font-heading tracking-tight text-foreground">
              {lang === 'es' ? 'Bienvenido' : 'Welcome back'}
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {lang === 'es' ? 'Accede a tu portal de trabajo' : 'Sign in to your workspace'}
            </p>
          </div>

          {/* SSO - única opción de acceso */}
          <button
            onClick={handleSSO}
            disabled={ssoLoading}
            data-testid="sso-login-btn"
            className="w-full h-11 flex items-center justify-center gap-3 rounded-lg border border-border bg-card hover:bg-muted/60 transition-all font-medium text-sm shadow-sm mb-4"
          >
            {ssoLoading ? (
              <Loader className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
            )}
            {ssoLoading
              ? (lang === 'es' ? 'Conectando...' : 'Connecting...')
              : 'Iniciar sesión con tu cuenta de Valoriza SM'
            }
          </button>

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive" data-testid="login-error">
              {error}
            </div>
          )}

          <p className="text-xs text-muted-foreground text-center mt-6">
            {lang === 'es'
              ? 'Al iniciar sesión aceptas las políticas corporativas de Valoriza'
              : 'By signing in you accept Valoriza corporate policies'}
          </p>
        </div>
      </div>

      {/* Right: Creative hero image */}
      <div className="hidden md:flex w-[55%] relative overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Valoriza campus"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlay with green brand tint */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/20 to-brand/40" />
        {/* Decorative orb */}
        <div className="absolute top-1/4 right-1/4 w-64 h-64 rounded-full bg-brand/20 blur-3xl" />

        {/* Content */}
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          {/* Top badge */}
          <div className="flex items-center gap-2.5">
            <span className="h-2 w-2 rounded-full bg-brand animate-pulse" />
            <span className="text-xs font-medium text-white/70 uppercase tracking-widest">
              {lang === 'es' ? 'Plataforma Corporativa' : 'Corporate Platform'}
            </span>
          </div>

          {/* Center stats */}
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '24', label: lang === 'es' ? 'Días vacaciones' : 'Vacation days' },
                { value: '6', label: lang === 'es' ? 'Días teletrabajo' : 'Remote days' },
              ].map(stat => (
                <div key={stat.label} className="text-white">
                  <div className="text-3xl font-bold font-heading">{stat.value}</div>
                  <div className="text-xs text-white/60 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>

            <div>
              <h2 className="text-3xl font-bold font-heading text-white leading-tight mb-2">
                {lang === 'es'
                  ? 'Gestiona tu tiempo,\nmejora tu bienestar'
                  : 'Manage your time,\nimprove your wellbeing'}
              </h2>
              <p className="text-sm text-white/70 max-w-xs">
                {lang === 'es'
                  ? 'Teletrabajo · Vacaciones · Aprobaciones · Fichajes'
                  : 'Remote Work · Vacation · Approvals · Clock-ins'}
              </p>
            </div>
          </div>

          {/* Bottom */}
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-white/20" />
            <span className="text-xs text-white/40 italic">powered by Valoriza's Innovation Team</span>
          </div>
        </div>
      </div>
    </div>
  );
}
